import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Instagram, Menu, MessageCircle, Phone, Search, ShoppingBag, Star, X } from "lucide-react";
import { CONFIG, NAV_LINKS, OFFERS, waLink } from "../config";
import { trackEvent as track } from "../lib/analytics";
import { useCart } from "../store";

export function WhatsAppButton({ label = "WhatsApp Us", context = "", className = "", testId }) {
  const id = testId || `${label.toLowerCase().replaceAll(" ", "-")}-cta`;
  return (
    <a
      data-testid={id}
      className={`whatsapp ${className}`}
      href={waLink(context)}
      target="_blank"
      rel="noreferrer"
      onClick={() => track("whatsapp_click", { context })}
    >
      <MessageCircle size={16} />
      {label}
    </a>
  );
}

export function FloatingWhatsApp() {
  return (
    <motion.a
      data-testid="floating-whatsapp-button"
      className="floating-wa"
      href={waLink()}
      target="_blank"
      rel="noreferrer"
      aria-label="Chat on WhatsApp"
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 1.4, type: "spring", stiffness: 200, damping: 14 }}
      onClick={() => track("whatsapp_click", { context: "floating" })}
    >
      <MessageCircle size={24} />
      <span className="wa-pulse" />
    </motion.a>
  );
}

function BrandMark() {
  return (
    <Link data-testid="brand-logo-link" to="/" className="brand" aria-label="Hyderabad Shopping Mall — home">
      {CONFIG.logo ? (
        <img src={CONFIG.logo} alt="Hyderabad Shopping Mall logo" className="brand-logo" />
      ) : (
        <>
          <span className="brand-mark">H</span>
          <span className="brand-word">
            HYDERABAD
            <br />
            <b>SHOPPING MALL</b>
          </span>
        </>
      )}
    </Link>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const cart = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <>
      <div className="announcement" data-testid="announcement-bar">
        <span>{CONFIG.announcementText}</span>
        <Link data-testid="announcement-offers-link" to="/offers" onClick={() => track("offer_click", { source: "announcement" })}>
          VIEW OFFERS <ArrowRight size={12} />
        </Link>
      </div>
      <header className={`site-header ${scrolled ? "scrolled" : ""}`}>
        <BrandMark />
        <nav className="nav-desktop" aria-label="Primary">
          {NAV_LINKS.map(([label, path]) => (
            <Link data-testid={`nav-${label.toLowerCase().replaceAll(" ", "-")}`} key={path} to={path}>
              {label}
            </Link>
          ))}
        </nav>
        <div className="header-actions">
          <button data-testid="search-button" className="icon-button" aria-label="Browse collections" onClick={() => navigate("/collections")}>
            <Search size={19} />
          </button>
          <Link data-testid="cart-button" className="icon-button cart-icon" to="/cart" aria-label="Shopping bag">
            <ShoppingBag size={19} />
            {cart?.count > 0 && <span className="cart-count" data-testid="cart-count">{cart.count}</span>}
          </Link>
          <WhatsAppButton label="WhatsApp" testId="header-whatsapp-cta" />
          <button data-testid="mobile-menu-button" className="mobile-menu" onClick={() => setOpen(!open)} aria-label="Toggle menu" aria-expanded={open}>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>
      <motion.div
        className={`mobile-nav ${open ? "open" : ""}`}
        initial={false}
        animate={open ? { opacity: 1, pointerEvents: "auto" } : { opacity: 0, pointerEvents: "none" }}
        transition={{ duration: 0.35 }}
      >
        <nav aria-label="Mobile">
          {NAV_LINKS.map(([label, path], i) => (
            <motion.div key={path} initial={false} animate={open ? { opacity: 1, x: 0 } : { opacity: 0, x: -18 }} transition={{ delay: open ? 0.06 * i : 0 }}>
              <Link data-testid={`mobile-nav-${label.toLowerCase().replaceAll(" ", "-")}`} to={path} onClick={() => setOpen(false)}>
                <span>0{i + 1}</span>
                {label}
                <ArrowUpRight size={18} />
              </Link>
            </motion.div>
          ))}
        </nav>
        <WhatsAppButton label="Chat on WhatsApp" testId="mobile-nav-whatsapp-cta" className="button-like" />
      </motion.div>
    </>
  );
}

// One slow editorial marquee — the signature rhythm of the site.
export function Marquee() {
  const items = OFFERS.map((o) => `${o.title} — ${o.detail}`);
  const row = [...items, ...items];
  return (
    <div className="marquee" data-testid="offers-marquee" aria-hidden="true">
      <div className="marquee-track">
        {row.map((item, i) => (
          <span key={i}>
            {item}
            <i className="diamond" />
          </span>
        ))}
      </div>
    </div>
  );
}

export function Footer() {
  return (
    <footer>
      <div className="footer-marquee" aria-hidden="true">
        <span>FASHION FOR EVERY OCCASION</span>
        <span className="outline">HYDERABAD SHOPPING MALL</span>
        <span>NANDED</span>
      </div>
      <div className="footer-top">
        <div className="footer-brand-block">
          <BrandMark />
          <p>{CONFIG.tagline}</p>
          <div className="footer-social">
            <a data-testid="footer-instagram-link" href={CONFIG.instagram} target="_blank" rel="noreferrer" aria-label="Instagram">
              <Instagram size={18} />
            </a>
            <a data-testid="footer-whatsapp-icon-link" href={waLink()} target="_blank" rel="noreferrer" aria-label="WhatsApp">
              <MessageCircle size={18} />
            </a>
            <a data-testid="footer-phone-icon-link" href={`tel:${CONFIG.phone}`} aria-label="Call the store" onClick={() => track("call_click")}>
              <Phone size={18} />
            </a>
          </div>
        </div>
        <div className="footer-columns">
          <div>
            <b>Explore</b>
            <Link data-testid="footer-collections-link" to="/collections">Collections</Link>
            <Link data-testid="footer-offers-link" to="/offers">Offers</Link>
            <Link data-testid="footer-wedding-link" to="/collections/wedding">Wedding Collection</Link>
            <Link data-testid="footer-new-arrivals-link" to="/new-arrivals">New Arrivals</Link>
            <Link data-testid="footer-about-link" to="/about">About Us</Link>
          </div>
          <div>
            <b>Collections</b>
            <Link data-testid="footer-sarees-link" to="/collections/sarees">Sarees</Link>
            <Link data-testid="footer-kurtis-link" to="/collections/kurtis">Kurtis</Link>
            <Link data-testid="footer-suits-link" to="/collections/unstitched-suits">Unstitched Suits</Link>
            <Link data-testid="footer-party-link" to="/collections/party-wear">Party Wear</Link>
            <Link data-testid="footer-kids-link" to="/collections/kids-wear">Kids Wear</Link>
            <Link data-testid="footer-western-link" to="/collections/western-collection">Western Collection</Link>
          </div>
          <div>
            <b>Visit Us</b>
            <span data-testid="footer-address" className="footer-address">{CONFIG.address}</span>
            <span data-testid="footer-hours" className="footer-address">Open: {CONFIG.hoursDisplay}</span>
            <a data-testid="footer-call-link" href={`tel:${CONFIG.phone}`}>Call: {CONFIG.phoneDisplay}</a>
            <Link data-testid="footer-track-order-link" to="/track">Track Order</Link>
            <a data-testid="footer-whatsapp-link" href={waLink()} target="_blank" rel="noreferrer">WhatsApp Us</a>
            <a data-testid="footer-directions-link" href={CONFIG.mapUrl} target="_blank" rel="noreferrer">Get Directions</a>
            <a data-testid="footer-google-review-link" href={CONFIG.mapUrl} target="_blank" rel="noreferrer" className="review-link">
              <Star size={13} /> Rate us on Google
            </a>
          </div>
        </div>
      </div>
      <div className="footer-bottom">
        <span>© 2026 Hyderabad Shopping Mall. All Rights Reserved.</span>
        <span>
          <Link data-testid="footer-privacy-link" to="/privacy">Privacy Policy</Link> ·{" "}
          <Link data-testid="footer-terms-link" to="/terms">Terms &amp; Conditions</Link>
        </span>
      </div>
    </footer>
  );
}
